import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ButtonColor } from '@components/buttons/button/button.component';
import { AlertDialogComponent } from '@components/modals/alert-dialog/alert-dialog.component';
import {
  ConfirmDialogButton,
  ConfirmDialogComponent,
  ConfirmHeader
} from '@components/modals/confirm-dialog/confirm-dialog.component';
import {
  ImportConfirmDialogButton,
  ImportDataDialogComponent,
  ImportDataDialogResponse
} from '@components/modals/import-data-dialog/import-data-dialog.component';
import { InfoDialogComponent } from '@components/modals/info-dialog/info-dialog.component';
import { WaitDialogComponent } from '@components/modals/wait-dialog/wait-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DialogService {
  constructor(
    @Inject('DialogWidth') private width: number,
    public dialog: MatDialog,
    private translateService: TranslateService
  ) {}

  alert(title: string, message: string) {
    return this.dialog.open(AlertDialogComponent, {
      width: this.width + 'px',
      data: {
        title,
        message
      }
    });
  }

  confirm(
    header: ConfirmHeader,
    message: string,
    yesButton: ConfirmDialogButton = {
      label: this.translateService.instant('core.general.accept'),
      color: ButtonColor.PRIMARY
    },
    noButton: ConfirmDialogButton = {
      label: this.translateService.instant('core.general.cancel')
    },
    width: number = this.width,
    secondaryMessage?: string
  ): Observable<boolean> {
    return this.dialog
      .open(ConfirmDialogComponent, {
        width: '485px',
        data: {
          header,
          message,
          secondaryMessage,
          yesButton,
          noButton
        }
      })
      .afterClosed();
  }

  info(title: string, message: string) {
    return this.dialog.open(InfoDialogComponent, {
      width: this.width + 'px',
      data: {
        title,
        message
      }
    });
  }

  wait(title: string = 'core.wait.title', message: string = 'core.wait.description', message2: string = '') {
    return this.dialog.open(WaitDialogComponent, {
      width: this.width + 'px',
      data: {
        title,
        message,
        message2
      }
    });
  }

  /**
   * Open a custom dialog
   *
   * NOTE: the component must be the same module that this service.
   * If you reference a component of another module (e.g. user) with method will
   * fail with this error:
   *     No component factory found for MyComponent. Did you add it to @NgModule.entryComponents?
   * @param component
   * @param panelClassName
   */
  customDialog(component: any, panelClassName?: string) {
    return this.dialog
      .open(component, {
        panelClass: panelClassName
      })
      .afterClosed();
  }

  /**
   * The dialog to import data
   *
   * @param type the import type (e.g. decoded)
   * @param header the header (e.g. Import users)
   * @param messageTemplate the message to show template info
   * @param templateType optional parameter that indicates that a template is available (null if not)
   * @param imageType optional parameter that indicates that the dialog support import images
   * @param yesButton configurable yes button
   * @param noButton configurable no button
   * @return an Observable
   */
  importData(
    type: string,
    templateType: string,
    helpLink: string,
    yesButton: ImportConfirmDialogButton = {
      label: this.translateService.instant('core.import.startImport'),
      color: ButtonColor.PRIMARY
    },
    noButton: ImportConfirmDialogButton = {
      label: this.translateService.instant('core.general.cancel')
    }
  ): Observable<ImportDataDialogResponse> {
    return this.dialog
      .open(ImportDataDialogComponent, {
        data: {
          type,
          templateType,
          helpLink,
          yesButton,
          noButton
        }
      })
      .afterClosed();
  }
}
